"use client";

import { useQuery } from "@tanstack/react-query";
import type { Address } from "viem";

import { TTL } from "@/config/ttl";
import type { Pool, TokenInfo } from "@/domain/types";
import { apiFetch } from "@/lib/apiFetch";

interface WirePool {
  address: Address;
  token0: TokenInfo;
  token1: TokenInfo;
  reserve0: string;
  reserve1: string;
  totalSupply: string;
}

function revive(p: WirePool): Pool {
  return {
    ...p,
    reserve0: BigInt(p.reserve0),
    reserve1: BigInt(p.reserve1),
    totalSupply: BigInt(p.totalSupply),
  };
}

async function fetchV2Pools(): Promise<Pool[]> {
  const data = await apiFetch<{ pools: WirePool[] }>("/api/v2/pools");
  return data.pools.map(revive);
}

export function useV2Pools() {
  return useQuery({
    queryKey: ["v2-pools"],
    queryFn: fetchV2Pools,
    staleTime: TTL.v2Pools,
  });
}

export interface PairReserves {
  pairAddress: Address;
  reserveA: bigint;
  reserveB: bigint;
}

interface WireReserves {
  pairAddress: Address;
  reserveA: string;
  reserveB: string;
}

async function fetchV2Reserves(tokenA: Address, tokenB: Address): Promise<PairReserves | null> {
  const data = await apiFetch<{ reserves: WireReserves | null }>(`/api/v2/quote?tokenA=${tokenA}&tokenB=${tokenB}`);
  if (!data.reserves) return null;
  return { ...data.reserves, reserveA: BigInt(data.reserves.reserveA), reserveB: BigInt(data.reserves.reserveB) };
}

export function useV2Reserves(tokenA: Address | undefined, tokenB: Address | undefined) {
  return useQuery({
    queryKey: ["v2-reserves", tokenA, tokenB],
    queryFn: () => fetchV2Reserves(tokenA as Address, tokenB as Address),
    enabled: Boolean(tokenA && tokenB && tokenA.toLowerCase() !== tokenB.toLowerCase()),
    staleTime: TTL.v2Reserves,
    refetchInterval: TTL.v2Reserves,
  });
}
